import { useEffect, useState } from 'react';
import type * as React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { LogOut, Menu, Moon, Sun, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dialog';
import { Mono, TooltipProvider } from '@/components/ui/primitives';
import { navFor, ROLE_LABELS } from '@/auth/rbac';
import { useSession } from '@/auth/use-session';
import { clearSession, IDLE_TIMEOUT_MS, touchSession } from '@/auth/session';
import { useTheme } from './theme';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from '@/components/error-boundary';

/**
 * The panel frame: a fixed left rail of the sections this role is shown, and a
 * scrolling content column.
 *
 * ⚠️ The rail is presentation. Every section it hides is still refused by the
 * server, and every section it shows is still checked there — see `auth/rbac.ts`.
 */
export function AdminLayout(): React.JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  useIdleLogout(() => {
    clearSession();
    void navigate('/login?reason=idle', { replace: true });
  });

  if (!session) return <></>;

  const sections = navFor(session.role);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-h-screen bg-canvas">
        <a
          href="#main"
          className="sr-only rounded bg-primary px-3 py-2 text-primary-ink focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50"
        >
          Skip to content
        </a>

        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line bg-surface transition-transform lg:translate-x-0',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-14 items-center justify-between gap-2 border-b border-line px-4">
            <div className="flex items-center">
              <Logo className="h-6" />
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="lg:hidden"
              aria-label="Close navigation"
              onClick={() => setMobileOpen(false)}
            >
              <X aria-hidden="true" />
            </Button>
          </div>

          <nav aria-label="Sections" className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
            {sections.map((section) => (
              <div key={section.label}>
                <p className="px-2 pb-1.5 text-2xs font-semibold tracking-wider text-ink-faint uppercase">
                  {section.label}
                </p>
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.path === '/'}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'group relative flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                            isActive
                              ? 'bg-primary-wash font-medium text-ink'
                              : 'text-ink-soft hover:bg-surface-sunken hover:text-ink',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span
                              aria-hidden="true"
                              className={cn(
                                'absolute top-1.5 bottom-1.5 -left-3 w-0.5 rounded-r bg-primary transition-opacity',
                                isActive ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <span className="truncate">{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="border-t border-line p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-surface-sunken"
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-2xs font-semibold text-primary-ink">
                    {ROLE_LABELS[session.role].slice(0, 1)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-ink">
                      {ROLE_LABELS[session.role]}
                    </span>
                    <span className="block truncate font-mono text-2xs text-ink-faint">
                      {session.adminId.slice(0, 8)}
                    </span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top">
                {/* No name or email: the panel has no endpoint that returns the
                    signed-in admin's own profile, and guessing at one would put
                    an address in every layout render, ANALYST screens included. */}
                <DropdownMenuLabel>Signed in</DropdownMenuLabel>
                <div className="px-2 pb-2">
                  <Mono>{session.adminId}</Mono>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={toggle}>
                  {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
                  {theme === 'dark' ? 'Light theme' : 'Dark theme'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  destructive
                  onSelect={() => {
                    clearSession();
                    void navigate('/login', { replace: true });
                  }}
                >
                  <LogOut className="size-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {mobileOpen ? (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}

        <div className="lg:pl-60">
          <div className="flex h-14 items-center gap-3 border-b border-line bg-surface px-4 lg:hidden">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <Menu aria-hidden="true" />
            </Button>
            <Logo className="h-6" />
            <span aria-hidden="true" className="h-4 w-px bg-line-strong" />
            <span className="text-sm font-semibold text-ink-soft">Admin</span>
          </div>

          <main id="main">
            {/* Per-route, so a screen that throws does not take the sidebar
                and navigation down with it — the panel stays usable. */}
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * Signs an idle admin out after 30 minutes (06 §3).
 *
 * ⚠️ A courtesy, not a boundary — the server expires the token on its own
 * schedule and is the only thing that actually ends access. This exists so an
 * unattended screen does not sit on a live session showing family data.
 */
function useIdleLogout(onIdle: () => void): void {
  useEffect(() => {
    let timer = window.setTimeout(onIdle, IDLE_TIMEOUT_MS);

    const reset = (): void => {
      touchSession();
      window.clearTimeout(timer);
      timer = window.setTimeout(onIdle, IDLE_TIMEOUT_MS);
    };

    const events = ['pointerdown', 'keydown', 'visibilitychange'] as const;
    for (const event of events) window.addEventListener(event, reset, { passive: true });

    return () => {
      window.clearTimeout(timer);
      for (const event of events) window.removeEventListener(event, reset);
    };
  }, [onIdle]);
}
